import ApiHelper from '../ApiHelper.js';
import {BackgroundLibraryScope} from "@/scripts/common/enums.js";

const AdminApiUrl = "/api/admin/";
const ShowcaseApiUrl = AdminApiUrl + "showcase/";

function uploadImageFromBlob(url, blob, fileName, path, filesToDelete) {
    const data = new FormData();
    data.append("imageFile", blob, fileName);
    data.append("path", path);
    data.append("filesToDelete", filesToDelete || []);
    return ApiHelper.postMultipart(url, data);
}

export default {

    getUsers: function (page, pageSize, orderBy, orderAscending, findBy, findByValue, hideDeletedUsers = false, publishedOnly = false, provenanceFilter = null) {
        return ApiHelper.getWithParams(AdminApiUrl + "users",
            {
                page: page,
                pageSize: pageSize,
                orderBy: orderBy,
                orderAscending: orderAscending,
                findBy: findBy,
                findByValue: findByValue,
                hideDeletedUsers: hideDeletedUsers,
                publishedOnly: publishedOnly,
                provenanceFilter: provenanceFilter
            }
        );
    },

    getUserCount: function (hideDeletedUsers = false, publishedOnly = false, provenanceFilter = null) {
        return ApiHelper.getWithParams(AdminApiUrl + "users/count", {
            hideDeletedUsers: hideDeletedUsers,
            publishedOnly: publishedOnly,
            provenanceFilter: provenanceFilter
        });
    },

    getUserSettings: function (userId) {
        return ApiHelper.get(AdminApiUrl + "user/" + userId);
    },

    changeUserVipStatus: function (userId) {
        return ApiHelper.post(AdminApiUrl + "changeVip/" + userId );
    },

    getSubscriptionReport: function (startDateTime) {
        return ApiHelper.get(AdminApiUrl + "report/subscription");
    },

    getDomainDetails: function () {
        return ApiHelper.getWithParams(AdminApiUrl + "report/domainDetails");
    },

    getYearlySubscriptionReserveDetails: function () {
        return ApiHelper.get(AdminApiUrl + "report/yearlySubscriptionReserveDetails");
    },

    getProjects: function (page, pageSize, minProjectsCount, userId) {
        return ApiHelper.getWithParams(AdminApiUrl + "projects",
            {
                page: page,
                pageSize: pageSize,
                minProjectsCount: minProjectsCount,
                userId: userId
            });
    },

    getAllCaseStudyPages(userId) {
        return ApiHelper.getWithParams(AdminApiUrl + `all/${userId}/caseStudy`)
    },

    getAllPortfolios(userId) {
        return ApiHelper.getWithParams(AdminApiUrl + `all/${userId}/portfolio`)
    },

    getShowcase: function () {
        return ApiHelper.get(ShowcaseApiUrl);
    },

    addShowcaseItem: function (newShowcaseItem) {
        return ApiHelper.post(ShowcaseApiUrl, newShowcaseItem);
    },

    modifyShowcase: function (showcase) {
        return ApiHelper.put(ShowcaseApiUrl, showcase);
    },

    modifyShowcaseItem: function (showcaseItem) {
        return ApiHelper.put(ShowcaseApiUrl + showcaseItem._id, showcaseItem);
    },

    deleteShowcaseItem: function (showcaseItem) {
        return ApiHelper.delete(ShowcaseApiUrl + showcaseItem._id, showcaseItem);
    },

    deleteShowcaseImage: function (projectId, imageUrl) {
        return ApiHelper.delete("/api/image/showcase/delete/" + projectId, {url: imageUrl});
    },

    getExpertReviewRequests: function (unanswered, page, pageSize) {
        return ApiHelper.getWithParams(AdminApiUrl + "review/expert",
            {
                unanswered: unanswered,
                page: page,
                pageSize: pageSize
            });
    },

    getCommunityReviews: function (page, pageSize) {
        return ApiHelper.getWithParams(AdminApiUrl + "review/community",
            {
                page: page,
                pageSize: pageSize
            });
    },

    checkRenenwalInSync: function () {
        return ApiHelper.get(AdminApiUrl + "owndomain/checkRenenwalInSync");
    },

    getAllGodaddyDomains: function () {
        return ApiHelper.get(AdminApiUrl + "owndomain/getAllGodaddyDomains");
    },

    getAllExternalDomains: function () {
        return ApiHelper.get(AdminApiUrl + "owndomain/getAllExternalDomains");
    },

    getOldProxyDomains: function () {
        return ApiHelper.get(AdminApiUrl + "owndomain/getOldProxyDomains");
    },

    getRecordDetails: function (domain) {
        return ApiHelper.get(AdminApiUrl + "owndomain/getRecordDetails/" + domain);
    },

    updateDomainRecords: function (domain) {
        return ApiHelper.post(AdminApiUrl + "owndomain/updateDomainRecords/" + domain);
    },

    checkDomainDetails: function (domain) {
        return ApiHelper.get(AdminApiUrl + "owndomain/checkDomainDetails/" + domain);
    },

    checkTldProfitMargin: function () {
        return ApiHelper.get(AdminApiUrl + "owndomain/checkTldProfitMargin");
    },

    getEmergencyModeSettings: function () {
        return ApiHelper.get(AdminApiUrl + "emergency/settings/get");
    },

    createEmergencyModeSettings: function () {
        return ApiHelper.post(AdminApiUrl + "emergency/settings/create");
    },

    updateEmergencyModeSettings: function (emergencyModeSettings) {
        return ApiHelper.put(AdminApiUrl + "emergency/settings/update", emergencyModeSettings);
    },

    getBackgroundLibrary(scope = BackgroundLibraryScope.THUMBNAILS) {
        return ApiHelper.getWithParams(AdminApiUrl + "background-library/", { scope });
    },

    updateBackgroundLibrary(items, scope = BackgroundLibraryScope.THUMBNAILS) {
        return ApiHelper.put(AdminApiUrl + "background-library/?scope=" + scope, items);
    },

    deleteBackgroundLibraryItem(itemId, scope = BackgroundLibraryScope.THUMBNAILS) {
        return ApiHelper.delete(AdminApiUrl + "background-library/" + itemId + "?scope=" + scope);
    },

    getColorPalettes(includeDeleted = true) {
        return ApiHelper.getWithParams(AdminApiUrl + "color-palettes", { includeDeleted });
    },

    addColorPalette(colorPalette) {
        return ApiHelper.post(AdminApiUrl + "color-palettes", colorPalette);
    },

    updateColorPalette(colorPaletteId, colorPalette) {
        return ApiHelper.put(AdminApiUrl + "color-palettes/" + colorPaletteId, colorPalette);
    },

    archiveColorPalette(colorPaletteId) {
        return ApiHelper.patch(AdminApiUrl + "color-palettes/" + colorPaletteId + "/archive");
    },

    restoreColorPalette(colorPaletteId) {
        return ApiHelper.patch(AdminApiUrl + "color-palettes/" + colorPaletteId + "/restore");
    },

    reorderColorPalettes(type, paletteIds) {
        return ApiHelper.put(AdminApiUrl + "color-palettes/reorder/" + type, { paletteIds });
    },

    getSectionPresetStructure(includeArchivedPresets = false) {
        return ApiHelper.getWithParams(AdminApiUrl + "section-presets/groups", { includeArchivedPresets });
    },

    createSectionPresetGroup(label) {
        return ApiHelper.post(AdminApiUrl + "section-presets/groups", { label });
    },

    renameSectionPresetGroup(groupKey, label) {
        return ApiHelper.put(AdminApiUrl + "section-presets/groups/" + groupKey, { label });
    },

    archiveSectionPresetGroup(groupKey) {
        return ApiHelper.delete(AdminApiUrl + "section-presets/groups/" + groupKey);
    },

    publishSectionPresetGroup(groupKey) {
        return ApiHelper.patch(AdminApiUrl + "section-presets/groups/" + groupKey + "/publish");
    },

    unpublishSectionPresetGroup(groupKey) {
        return ApiHelper.patch(AdminApiUrl + "section-presets/groups/" + groupKey + "/unpublish");
    },

    reorderSectionPresetGroups(groupKeys) {
        return ApiHelper.put(AdminApiUrl + "section-presets/groups/reorder", { groupKeys });
    },

    createSectionPresetSubgroup(groupKey, label) {
        return ApiHelper.post(AdminApiUrl + "section-presets/groups/" + groupKey + "/subgroups", { label });
    },

    renameSectionPresetSubgroup(groupKey, subgroupKey, label) {
        return ApiHelper.put(AdminApiUrl + "section-presets/groups/" + groupKey + "/subgroups/" + subgroupKey, { label });
    },

    archiveSectionPresetSubgroup(groupKey, subgroupKey) {
        return ApiHelper.delete(AdminApiUrl + "section-presets/groups/" + groupKey + "/subgroups/" + subgroupKey);
    },

    publishSectionPresetSubgroup(groupKey, subgroupKey) {
        return ApiHelper.patch(AdminApiUrl + "section-presets/groups/" + groupKey + "/subgroups/" + subgroupKey + "/publish");
    },

    unpublishSectionPresetSubgroup(groupKey, subgroupKey) {
        return ApiHelper.patch(AdminApiUrl + "section-presets/groups/" + groupKey + "/subgroups/" + subgroupKey + "/unpublish");
    },

    reorderSectionPresetSubgroups(groups) {
        return ApiHelper.put(AdminApiUrl + "section-presets/subgroups/reorder", { groups });
    },

    reorderSectionPresetsInSubgroups(subgroups) {
        return ApiHelper.put(AdminApiUrl + "section-presets/sections/reorder", { subgroups });
    },

    addSectionPresetToSubgroup(groupKey, subgroupKey, presetKey) {
        return ApiHelper.post(
            AdminApiUrl + "section-presets/groups/" + groupKey + "/subgroups/" + subgroupKey + "/sections",
            { presetKey }
        );
    },

    getAdminSectionPreset(presetKey) {
        return ApiHelper.get(AdminApiUrl + "section-presets/presets/" + presetKey);
    },

    createAdminSectionPreset(payload) {
        return ApiHelper.post(AdminApiUrl + "section-presets/presets", payload);
    },

    updateAdminSectionPreset(presetKey, payload) {
        return ApiHelper.put(AdminApiUrl + "section-presets/presets/" + presetKey, payload);
    },

    archiveAdminSectionPreset(presetKey) {
        return ApiHelper.patch(AdminApiUrl + "section-presets/presets/" + presetKey + "/archive");
    },

    restoreAdminSectionPreset(presetKey) {
        return ApiHelper.patch(AdminApiUrl + "section-presets/presets/" + presetKey + "/restore");
    },

    getAdminSectionPresetSections(presetKey) {
        return ApiHelper.get(AdminApiUrl + "section-presets/presets/" + presetKey + "/sections");
    },

    getAdminBaseSection(sectionType, mediaType = null) {
        return ApiHelper.getWithParams(AdminApiUrl + "section-presets/base-section", { sectionType, mediaType });
    },

    uploadSectionPresetImage(userId, blob, fileType, imageToDelete = null) {
        const normalizedUserId = typeof userId === "string" ? userId.trim() : "";
        const path = normalizedUserId
            ? `${normalizedUserId}/admin/section-presets/media`
            : "admin/section-presets/media";
        return uploadImageFromBlob(
            AdminApiUrl + "section-presets/image/upload",
            blob,
            `sectionPresetImage.${fileType}`,
            path,
            imageToDelete ? [imageToDelete] : []
        );
    },

    deleteSectionPresetImage(imageUrl, croppedImageUrl = null, options = {}) {
        return ApiHelper.delete(AdminApiUrl + "section-presets/image/delete", {
            url: imageUrl,
            croppedImageUrl
        }, options);
    },
}
